'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { VStack, useBreakpointValue, Box } from '@skin-seoul/design-system';
import ActionButtonGroup from './ActionButtonGroup';
interface ProductActionButtonsProps {
  onAddToBag: () => void;
  onBuyNow: () => void;
  sku: string;
  productStatus?: string;
  addToBagStatus?: string;
  hasSelectedOption: boolean;
  onWishlistToggle?: (action: 'add' | 'remove') => void;
}
const STICKY_BOTTOM_OFFSET = 63;
const BOTTOM_THRESHOLD = 100;
const STICKY_TRANSITION_DURATION = '0.3s ease';
const ProductActionButtons = ({
  onAddToBag,
  onBuyNow,
  sku,
  productStatus,
  addToBagStatus,
  hasSelectedOption,
  onWishlistToggle
}: ProductActionButtonsProps) => {
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpointValue({
    base: true,
    tablet: false
  });
  const calculateStickyState = useCallback((entry: IntersectionObserverEntry) => {
    const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - BOTTOM_THRESHOLD;
    const isScrolledDown = window.scrollY > 0;
    return !entry.isIntersecting && isScrolledDown && !isAtBottom;
  }, []);
  useEffect(() => {
    if (!isMobile) {
      setIsSticky(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      const shouldStick = calculateStickyState(entry);
      setIsSticky(shouldStick);
    }, {
      threshold: 0
    });
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [isMobile, calculateStickyState]);
  const actionButtonProps = {
    onAddToBag,
    onBuyNow,
    sku,
    productStatus,
    hasSelectedOption,
    onWishlistToggle
  };
  return <>
      <Box ref={sentinelRef} height="1px" data-sentry-element="Box" data-sentry-source-file="ProductActionButtons.tsx" />

      <VStack gap={2} width="100%" position="relative" bg="transparent" data-sentry-element="VStack" data-sentry-source-file="ProductActionButtons.tsx">
        {!isMobile && <Box marginX={{
        base: 0,
        tablet: 0
      }} marginY={-4} position="absolute" top={0} left={0} right={0} height="1px" background="neutral.75" pointerEvents="none" />}
        <ActionButtonGroup {...actionButtonProps} size="desktop" showBuyNow={true} addToBagStatus={addToBagStatus} data-sentry-element="ActionButtonGroup" data-sentry-source-file="ProductActionButtons.tsx" />

        <Box marginX={{
        base: 0,
        tablet: 0
      }} marginY={-4} position="absolute" bottom={0} left={0} right={0} height="1px" background="neutral.75" pointerEvents="none" data-sentry-element="Box" data-sentry-source-file="ProductActionButtons.tsx" />
      </VStack>

      <VStack gap={2} width="100%" position="fixed" bottom={`${STICKY_BOTTOM_OFFSET}px`} px={4} left={0} bg="white" boxShadow="0 -2px 10px rgba(0,0,0,0.05)" zIndex={8} opacity={isSticky ? 1 : 0} transform={isSticky ? 'translateY(0)' : 'translateY(60px)'} transition={`opacity ${STICKY_TRANSITION_DURATION}, transform ${STICKY_TRANSITION_DURATION}`} pointerEvents={isSticky ? 'auto' : 'none'} paddingBottom="env(safe-area-inset-bottom)" height="64px" paddingTop="8px" data-sentry-element="VStack" data-sentry-source-file="ProductActionButtons.tsx">
        <ActionButtonGroup {...actionButtonProps} size="mobile" showBuyNow={false} addToBagStatus={addToBagStatus} data-sentry-element="ActionButtonGroup" data-sentry-source-file="ProductActionButtons.tsx" />
      </VStack>
    </>;
};
export default ProductActionButtons;